// Thin client for the Laravel CMS API.
//
// Only used for things that cannot come from the build-time snapshot: the
// contact form (a write) and blog post bodies (excluded from the aggregate
// payload so it stays small as posts accumulate).

const BASE_URL = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '')

class ApiError extends Error {
  constructor (message, { status, errors } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errors = errors || {}
  }
}

async function request (path, { method = 'GET', body, timeout = 10000 } = {}) {
  if (!BASE_URL) {
    // Misconfiguration, but the visitor should still get a way forward — the
    // caller falls back to showing the contact email address.
    throw new ApiError('We could not reach the server.')
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(`${BASE_URL}/api/v1${path}`, {
      method,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    })

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      throw new ApiError(
        payload?.message || `Request failed (${response.status})`,
        { status: response.status, errors: payload?.errors }
      )
    }

    return payload
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('The request timed out. Please try again.')
    }
    if (error instanceof ApiError) {
      throw error
    }
    throw new ApiError('Could not reach the server. Please check your connection.')
  } finally {
    clearTimeout(timer)
  }
}

/** Full article body for one post. */
export const fetchBlogPost = (slug) => request(`/blog/${encodeURIComponent(slug)}`)

/** Submit the contact form. Resolves with { message }. */
export const submitContact = (form) => request('/contact', {
  method: 'POST',
  body: form,
  timeout: 15000
})

export { ApiError }
